import React, { useEffect, useLayoutEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ArrowRight, BrainCircuit, LayoutDashboard, ShieldCheck, PlayCircle, ChevronRight, Sparkles, Target } from 'lucide-react';
import { AndroidHead } from '../components/AndroidHead';
import { readHeroSessionState } from '../lib/sessionProgress';
import { useAuth } from '../contexts/AuthContext';
import { AGENTS, AGENT_LIST, AgentId } from '../config/agents';
import { sendFunnelEvent } from '../lib/funnel';

const AGENT_MENTION_REGEX = /(Nova|Mira|Rowan|Devon|Naomi)/g;

const AGENT_VISUALS: Record<AgentId, {
    mood: 'idle' | 'happy' | 'talking' | 'sleepy' | 'dead' | 'popcorn';
    facing: 'front' | 'left' | 'right';
    shellClass: string;
    chipClass: string;
    glow: string;
    why: string;
    signatureQuestion: string;
    launchPrompt: string;
}> = {
    orchestrator: {
        mood: 'happy',
        facing: 'right',
        shellClass: 'bg-cyan-500/10 border-cyan-400/30',
        chipClass: 'bg-cyan-500/10 text-cyan-200 border-cyan-400/30',
        glow: '0 22px 80px -44px rgba(6,182,212,0.95)',
        why: 'Nova reframes the problem, strips away vanity assumptions, and decides which specialist should take first pass.',
        signatureQuestion: 'What are we pretending is fixed that actually is not?',
        launchPrompt: 'Help me identify the real leverage point in this challenge.'
    },
    people: {
        mood: 'happy',
        facing: 'left',
        shellClass: 'bg-pink-500/10 border-pink-400/30',
        chipClass: 'bg-pink-500/10 text-pink-200 border-pink-400/30',
        glow: '0 22px 80px -44px rgba(236,72,153,0.95)',
        why: 'Mira catches the trust fractures, morale leaks, and leadership blind spots that quietly sabotage execution.',
        signatureQuestion: 'Where are people nodding in meetings but resisting in reality?',
        launchPrompt: 'Show me the people and trust risks hiding inside this problem.'
    },
    process: {
        mood: 'talking',
        facing: 'right',
        shellClass: 'bg-amber-500/10 border-amber-400/30',
        chipClass: 'bg-amber-500/10 text-amber-200 border-amber-400/30',
        glow: '0 22px 80px -44px rgba(245,158,11,0.95)',
        why: 'Rowan turns friction into clean flow, deletes unnecessary work, and sequences the next move so teams stop spinning.',
        signatureQuestion: 'What can we remove entirely before we try to optimize it?',
        launchPrompt: 'Turn this messy challenge into a cleaner operating flow.'
    },
    tech: {
        mood: 'idle',
        facing: 'left',
        shellClass: 'bg-violet-500/10 border-violet-400/30',
        chipClass: 'bg-violet-500/10 text-violet-200 border-violet-400/30',
        glow: '0 22px 80px -44px rgba(139,92,246,0.95)',
        why: 'Devon spots structural fragility, technical debt, and the systems decisions that either compound or choke your momentum.',
        signatureQuestion: 'Which workaround is quietly becoming your architecture?',
        launchPrompt: 'Expose the technical debt or systems risk inside this challenge.'
    },
    data: {
        mood: 'talking',
        facing: 'right',
        shellClass: 'bg-emerald-500/10 border-emerald-400/30',
        chipClass: 'bg-emerald-500/10 text-emerald-200 border-emerald-400/30',
        glow: '0 22px 80px -44px rgba(16,185,129,0.95)',
        why: 'Naomi converts fog into evidence, frames the cost of inaction, and shows what needs to be measured before you guess.',
        signatureQuestion: 'What number would force this decision out of opinion territory?',
        launchPrompt: 'Tell me what metrics or evidence should drive this decision.'
    }
};

const HERO_SCENARIOS = [
    {
        id: 'alignment-reset',
        label: 'Reset the room',
        eyebrow: 'Leadership alignment',
        prompt: 'Our team is moving fast, but every function has a different definition of success.',
        launchPrompt: 'Our team is moving fast, but every function has a different definition of success.',
        movement: 'Turn internal chaos into a single operating language.',
        leadAgentId: 'orchestrator' as AgentId,
        outcomes: ['Clarify the actual decision', 'Expose hidden trust leaks', 'Sequence the next two moves'],
        responses: [
            { agentId: 'orchestrator' as AgentId, cta: 'Hear Nova open', text: 'Nova: I would stop debating tactics and name the actual decision first. If success means five different things, you do not have an execution problem. You have a clarity problem.' },
            { agentId: 'people' as AgentId, cta: 'Loop Mira', text: 'Mira: I would inspect where leaders are performing alignment instead of practicing it. Misalignment usually survives because nobody wants to surface the political cost of saying the hard part out loud.' },
            { agentId: 'process' as AgentId, cta: 'Loop Rowan', text: 'Rowan: Give me one shared definition of done, one owner, and one weekly ritual where drift gets corrected immediately. Most alignment theatre dies the second the workflow gets explicit.' }
        ]
    },
    {
        id: 'shipping-speed',
        label: 'Ship faster',
        eyebrow: 'Delivery drag',
        prompt: 'We are shipping, but every release feels slower and more expensive than the last one.',
        launchPrompt: 'We are shipping, but every release feels slower and more expensive than the last one.',
        movement: 'Show teams a cleaner path from idea to release.',
        leadAgentId: 'process' as AgentId,
        outcomes: ['Cut waste before adding process', 'See tech drag in plain English', 'Focus the team on one unblocker'],
        responses: [
            { agentId: 'orchestrator' as AgentId, cta: 'Hear Nova open', text: 'Nova: The question is not "How do we work harder?" It is "Which drag source is multiplying across every release?" We go after the compounding bottleneck first.' },
            { agentId: 'process' as AgentId, cta: 'Loop Rowan', text: 'Rowan: I would map the path from request to release and mark every approval, handoff, and status ritual that adds delay without improving the outcome. Something in that chain wants to be deleted.' },
            { agentId: 'tech' as AgentId, cta: 'Loop Devon', text: 'Devon: Slow shipping often means invisible systems tax. A workaround that looked harmless three quarters ago may now be the thing every release has to tiptoe around.' }
        ]
    },
    {
        id: 'prove-demand',
        label: 'Prove demand',
        eyebrow: 'Product-market signal',
        prompt: 'We have interest, but not enough evidence to know what customers truly value yet.',
        launchPrompt: 'We have interest, but not enough evidence to know what customers truly value yet.',
        movement: 'Replace wishful thinking with decision-grade signal.',
        leadAgentId: 'data' as AgentId,
        outcomes: ['Turn hunches into testable signal', 'See what to measure next', 'Find the highest-value unknown'],
        responses: [
            { agentId: 'orchestrator' as AgentId, cta: 'Hear Nova open', text: 'Nova: Before we chase more surface-level feedback, we define the decision the data must unlock. Evidence is only useful when it changes what you are willing to do next.' },
            { agentId: 'data' as AgentId, cta: 'Loop Naomi', text: 'Naomi: I want the one metric or behavior that separates curiosity from commitment. If that signal is weak, the job is to instrument it quickly instead of arguing from vibes.' },
            { agentId: 'people' as AgentId, cta: 'Loop Mira', text: 'Mira: And I would listen for what customers are too polite to say directly. Some of the best product truth is hiding in hesitation, not in praise.' }
        ]
    },
    {
        id: 'culture-pressure',
        label: 'Hold the culture',
        eyebrow: 'Morale under pressure',
        prompt: 'The business is growing, but the team is getting tense, guarded, and harder to trust under pressure.',
        launchPrompt: 'The business is growing, but the team is getting tense, guarded, and harder to trust under pressure.',
        movement: 'Help teams scale pressure without becoming brittle.',
        leadAgentId: 'people' as AgentId,
        outcomes: ['Name the cultural debt', 'Protect speed without burning trust', 'Choose the first repair move'],
        responses: [
            { agentId: 'orchestrator' as AgentId, cta: 'Hear Nova open', text: 'Nova: Pressure does not create culture problems from nowhere. It reveals the ones you were already carrying. We need to identify the behavior that pressure is amplifying.' },
            { agentId: 'people' as AgentId, cta: 'Loop Mira', text: 'Mira: I would examine where fear is distorting honesty. If people are self-protecting, your leaders are not hearing reality early enough to act on it.' },
            { agentId: 'process' as AgentId, cta: 'Loop Rowan', text: 'Rowan: Then I would change the cadence. The team needs one safe ritual where blockers get surfaced early, before they metastasize into resentment or heroic cleanup.' }
        ]
    }
];



const MAX_INPUT_CHARS = 500; // (#16) Character limit for input

const PLACEHOLDER_TEXTS = [
    "What's the hardest call you're avoiding right now\u2026",
    "My team can't agree on Q3 priorities\u2026",
    "We're shipping but nothing is landing\u2026",
    "Can't get buy-in for this decision\u2026",
    "Something is slowing us down but I can't name it\u2026",
    "The roadmap feels wrong but I can't explain why\u2026",
    "We have momentum but no clarity\u2026",
];

function LandingPage() {
    const navigate = useNavigate();
    const location = useLocation();
    const { user, signInWithGoogle } = useAuth();
    const [input, setInput] = useState('');
    const [isFocused, setIsFocused] = useState(false);
    const [placeholderIdx, setPlaceholderIdx] = useState(0);
    const [placeholderFading, setPlaceholderFading] = useState(false);
    const [hasInteracted, setHasInteracted] = useState(false); // Persistent state

    const [selectedAgentId, setSelectedAgentId] = useState<AgentId>('orchestrator');
    const [activeScenarioId, setActiveScenarioId] = useState<(typeof HERO_SCENARIOS)[number]['id']>(HERO_SCENARIOS[0].id);
    const [revealedResponseCount, setRevealedResponseCount] = useState(1);

    const [scrollY, setScrollY] = useState(0);
    const [scrollProgress, setScrollProgress] = useState(0);

    useEffect(() => {
        const container = document.getElementById('main-content');
        if (!container) return;
        const handleScroll = () => {
            const nextY = container.scrollTop;
            const maxScroll = Math.max(1, container.scrollHeight - container.clientHeight);
            setScrollY(nextY);
            setScrollProgress(Math.min(1, nextY / maxScroll));
        };
        handleScroll(); // Initial measure
        container.addEventListener('scroll', handleScroll, { passive: true });
        return () => container.removeEventListener('scroll', handleScroll);
    }, []);

    useEffect(() => {
        // Clear legacy landing-page prompt persistence. The previous prompt should
        // not stick around once the user has already acted on it.
        try { localStorage.removeItem('crucible_last_query'); } catch { /* ignore */ }
        void sendFunnelEvent({ type: 'landing_view', email: '', source: 'landing' });
    }, []);

    // Cycle placeholder text when input is idle
    useEffect(() => {
        if (input.trim().length > 0 || isFocused) return;
        const timer = setInterval(() => {
            setPlaceholderFading(true);
            const swap = setTimeout(() => {
                setPlaceholderIdx((i) => (i + 1) % PLACEHOLDER_TEXTS.length);
                setPlaceholderFading(false);
            }, 350);
            return () => clearTimeout(swap);
        }, 3800);
        return () => clearInterval(timer);
    }, [input, isFocused]);

    const heroSession = readHeroSessionState();
    const locationState = (location.state || null) as { timedOut?: boolean; timeoutReason?: string } | null;
    const showTimeoutNotice = Boolean(locationState?.timedOut);
    const timeoutReason = locationState?.timeoutReason || 'Session timed out. Please continue from the hero.';


    const activeScenario = HERO_SCENARIOS.find((scenario) => scenario.id === activeScenarioId) ?? HERO_SCENARIOS[0];
    const selectedAgent = AGENTS[selectedAgentId];
    const selectedAgentVisual = AGENT_VISUALS[selectedAgentId];
    const visibleResponses = activeScenario.responses.slice(0, revealedResponseCount);
    const nextResponse = activeScenario.responses[revealedResponseCount];
    const selectedAgentScenario = HERO_SCENARIOS.find((scenario) => scenario.leadAgentId === selectedAgentId) ?? activeScenario;

    const handleScenarioSelect = (scenarioId: (typeof HERO_SCENARIOS)[number]['id']) => {
        const scenario = HERO_SCENARIOS.find((item) => item.id === scenarioId);
        void sendFunnelEvent({ type: 'scenario_click', email: '', source: 'landing', meta: { scenario: scenarioId } });
        React.startTransition(() => {
            setActiveScenarioId(scenarioId);
            setSelectedAgentId(scenario?.leadAgentId ?? 'orchestrator');
            setRevealedResponseCount(1);
            setHasInteracted(true);
        });
    };

    const handleAdvanceConversation = () => {
        setHasInteracted(true);
        setRevealedResponseCount((current) => Math.min(current + 1, activeScenario.responses.length));
    };

    const handleUseScenarioPrompt = (prompt: string) => {
        setInput(prompt.slice(0, MAX_INPUT_CHARS));
        setHasInteracted(true);
    };

    const hasText = input.trim().length > 0;
    const heroShift = Math.min(scrollY * 0.18, 90);
    const heroScale = Math.max(0.86, 1 - scrollProgress * 0.12);
    const heroOpacity = Math.max(0.60, 1 - scrollProgress * 0.92);
    const panelShift = Math.min(scrollY * 0.1, 28);
    const infoOpacity = Math.max(0.42, 1 - scrollProgress * 0.45);

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        const trimmed = input.trim();
        if (!trimmed) return;
        try { localStorage.removeItem('crucible_last_query'); } catch { /* ignore */ }
        void sendFunnelEvent({ type: 'input_submit', email: '', source: 'landing', meta: { chars: trimmed.length } });
        setInput('');
        navigate('/crucible', { state: { initialQuery: trimmed } });
    };

    const handleFocus = () => {
        setIsFocused(true);
        setHasInteracted(true);
    }

    return (
        <div className="min-h-[170vh] bg-[#050505] text-white font-sans selection:bg-cyan-500/30 overflow-x-clip relative">
            <div className="fixed top-0 left-0 right-0 z-40 pointer-events-none px-5 pt-3">
                <div className="mx-auto h-px w-full max-w-6xl rounded-full bg-white/5 overflow-hidden">
                    <div
                        className="h-full origin-left rounded-full bg-gradient-to-r from-cyan-400 via-blue-500 to-purple-500 shadow-[0_0_30px_rgba(59,130,246,0.55)] transition-transform duration-150 ease-out"
                        style={{ transform: `scaleX(${Math.max(scrollProgress, 0.03)})` }}
                    />
                </div>
            </div>

            {/* Background Gradients */}
            <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
                <div
                    className="absolute top-[-20%] left-[20%] w-[600px] h-[600px] bg-blue-900/20 rounded-full blur-[120px] animate-pulse-slow"
                    style={{ transform: `translate3d(${scrollY * 0.08}px, ${scrollY * -0.12}px, 0)` }}
                />
                <div
                    className="absolute bottom-[-10%] right-[10%] w-[500px] h-[500px] bg-purple-900/20 rounded-full blur-[100px]"
                    style={{ transform: `translate3d(${scrollY * -0.06}px, ${scrollY * 0.1}px, 0)` }}
                />
                <div
                    className="absolute top-[40%] left-[-10%] w-[400px] h-[400px] bg-emerald-900/10 rounded-full blur-[80px]"
                    style={{ transform: `translate3d(${scrollY * 0.04}px, ${scrollY * 0.16}px, 0)` }}
                />
            </div>

            {/* Grid Overlay */}
            <div
                className="absolute inset-0 bg-grid-white/[0.03] bg-[size:40px_40px] pointer-events-none transition-opacity duration-300"
                style={{ opacity: Math.max(0.18, 0.45 - scrollProgress * 0.22) }}
            />

            {/* Subtle Sign-in text — top right */}
            {!user && (
                <button
                    onClick={signInWithGoogle}
                    className="fixed top-6 right-8 z-50 text-xs text-slate-500 hover:text-slate-300 transition-colors cursor-pointer bg-transparent border-none font-light tracking-wide"
                >
                    Sign in
                </button>
            )}
            {user && (
                <span className="fixed top-6 right-8 z-50 text-xs text-slate-500 font-light tracking-wide">
                    {user.displayName || user.email}
                </span>
            )}

            {/* Main Content */}
            <main className="relative z-10 flex flex-col items-center p-6 w-full max-w-7xl mx-auto">
                {/* Hero Wrapper */}
                <div
                    className="flex flex-col items-center justify-center min-h-[84vh] w-full transition-[transform,opacity] duration-150 ease-out"
                    style={{
                        transform: `translate3d(0, ${heroShift}px, 0) scale(${heroScale})`,
                        opacity: heroOpacity,
                    }}
                >
                    {showTimeoutNotice && (
                        <div className="w-full max-w-3xl mb-6 rounded-xl border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-sm text-amber-100 backdrop-blur-md">
                            <div className="font-semibold">Session Timeout</div>
                            <p className="text-amber-100/85 mt-1">{timeoutReason}</p>
                        </div>
                    )}

                    {/* Hero Text */}
                    <div className={`transition-all duration-1000 ease-[cubic-bezier(0.2,0.8,0.2,1)] mb-12 text-center flex flex-col items-center ${hasInteracted ? 'scale-95 opacity-0 pointer-events-none absolute -top-40' : 'scale-100 opacity-100 relative'}`}>
                        <div className="inline-flex items-center gap-3 px-4 py-2 mb-6 rounded-full border border-white/10 bg-white/5 backdrop-blur-sm shadow-[0_0_30px_rgba(6,182,212,0.15)]">
                            {[
                                { name: 'Nova',  color: 'bg-cyan-400',    glow: 'shadow-[0_0_6px_2px_rgba(34,211,238,0.7)]',   delay: '0ms'   },
                                { name: 'Mira',  color: 'bg-pink-400',    glow: 'shadow-[0_0_6px_2px_rgba(244,114,182,0.7)]',  delay: '150ms' },
                                { name: 'Rowan', color: 'bg-amber-400',   glow: 'shadow-[0_0_6px_2px_rgba(251,191,36,0.7)]',   delay: '300ms' },
                                { name: 'Devon', color: 'bg-violet-400',  glow: 'shadow-[0_0_6px_2px_rgba(167,139,250,0.7)]',  delay: '450ms' },
                                { name: 'Naomi', color: 'bg-emerald-400', glow: 'shadow-[0_0_6px_2px_rgba(52,211,153,0.7)]',   delay: '600ms' },
                            ].map(({ name, color, glow, delay }) => (
                                <div key={name} className="flex items-center gap-1.5">
                                    <span
                                        className={`w-1.5 h-1.5 rounded-full ${color} ${glow} animate-pulse`}
                                        style={{ animationDelay: delay }}
                                    />
                                    <span className="text-[10px] uppercase font-bold tracking-[0.15em] text-slate-400">{name}</span>
                                </div>
                            ))}
                            <span className="w-px h-3 bg-white/15 mx-1" />
                            <span className="text-[10px] uppercase font-bold tracking-[0.15em] text-slate-500">All online</span>
                        </div>
                        <h1 className="text-5xl md:text-7xl lg:text-[5.5rem] font-extrabold tracking-tighter mb-4 leading-[1.1]">
                            <span className="text-transparent bg-clip-text bg-gradient-to-r from-teal-400 via-cyan-400 to-purple-500 drop-shadow-[0_0_40px_rgba(6,182,212,0.4)]">
                                Hard Calls Need a Crew.
                            </span>
                        </h1>
                        <p className="text-slate-400 text-base md:text-lg font-mono tracking-wide mb-6">
                            Tell me your hardest problem right now.
                        </p>
                        <p className="text-slate-300 md:text-xl font-light tracking-wide max-w-2xl mx-auto leading-relaxed border-l-2 border-purple-500/50 pl-6 text-left">
                            Bring the half-formed idea, the leadership mess, the product doubt, or the systems knot. <button type="button" onClick={() => document.getElementById('meet-the-crew')?.scrollIntoView({ behavior: 'smooth' })} className="font-semibold text-cyan-400 hover:underline cursor-pointer">Nova</button>, <button type="button" onClick={() => document.getElementById('meet-the-crew')?.scrollIntoView({ behavior: 'smooth' })} className="font-semibold text-pink-400 hover:underline cursor-pointer">Mira</button>, <button type="button" onClick={() => document.getElementById('meet-the-crew')?.scrollIntoView({ behavior: 'smooth' })} className="font-semibold text-amber-400 hover:underline cursor-pointer">Rowan</button>, <button type="button" onClick={() => document.getElementById('meet-the-crew')?.scrollIntoView({ behavior: 'smooth' })} className="font-semibold text-violet-400 hover:underline cursor-pointer">Devon</button>, and <button type="button" onClick={() => document.getElementById('meet-the-crew')?.scrollIntoView({ behavior: 'smooth' })} className="font-semibold text-emerald-400 hover:underline cursor-pointer">Naomi</button> each pressure-test the problem from a different angle so you get a sharper next move instead of more vague advice.
                        </p>
                        {/* (#37) Trust badges / social proof */}
                        <div className="mt-8 flex flex-wrap items-center justify-center gap-8 text-[11px] text-slate-400 font-mono tracking-wider">
                            <div className="flex items-center gap-2 bg-white/5 px-3 py-1.5 rounded-lg border border-white/10 shadow-inner">
                                <svg className="w-4 h-4 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" /></svg>
                                <span className="uppercase font-semibold">Privacy-First</span>
                            </div>
                            <div className="flex items-center gap-2 bg-white/5 px-3 py-1.5 rounded-lg border border-white/10 shadow-inner">
                                <svg className="w-4 h-4 text-cyan-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>
                                <span className="uppercase font-semibold">Real-Time AI</span>
                            </div>
                            <div className="flex items-center gap-2 bg-white/5 px-3 py-1.5 rounded-lg border border-white/10 shadow-inner">
                                <svg className="w-4 h-4 text-purple-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg>
                                <span className="uppercase font-semibold">No Sign-Up Required</span>
                            </div>
                        </div>
                    </div>

                    {heroSession.trackerUnlocked && (
                        <div className="w-full max-w-3xl mb-8 rounded-2xl border border-emerald-400/30 bg-emerald-500/10 px-5 py-4 backdrop-blur-md">
                            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                                <div>
                                    <div className="text-xs uppercase tracking-widest text-emerald-300 font-bold">
                                        Tracker Unlocked
                                    </div>
                                    <p className="text-sm text-emerald-100/90">
                                        Your Eisenboard is at {heroSession.confidence}% confidence. Continue in Tracker or resume the board.
                                    </p>
                                </div>
                                <div className="flex gap-2">
                                    <button
                                        type="button"
                                        onClick={() => navigate('/tracker')}
                                        className="px-4 py-2 rounded-lg bg-emerald-500/20 border border-emerald-300/40 text-emerald-100 text-sm font-semibold hover:bg-emerald-500/30 transition-colors"
                                    >
                                        Open Tracker
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => navigate(heroSession.resumePath || '/crucible')}
                                        className="px-4 py-2 rounded-lg bg-white/10 border border-white/15 text-slate-100 text-sm font-semibold hover:bg-white/15 transition-colors"
                                    >
                                        Resume Session
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Glassmorphic Input Panel */}
                    <div
                        data-testid="landing-glass-panel"
                        className={`relative group transition-all duration-700 ease-[cubic-bezier(0.2,0.8,0.2,1)] w-full mt-10 rounded-xl ${hasText ? 'max-w-3xl scale-100' : 'max-w-3xl scale-105'}`}
                        style={{ transform: `translate3d(0, ${panelShift}px, 0)` }}
                    >
                        {/* Ominous Nova — peeking from behind the frosted glass */}
                        <div className="absolute -top-12 left-1/2 -translate-x-1/2 w-32 h-32 opacity-20 group-hover:opacity-100 transition-all duration-1000 ease-[cubic-bezier(0.4,0,0.2,1)] z-0 flex flex-col items-center group-hover:-translate-y-6 pointer-events-none">
                            <div className="w-24 h-24 bg-emerald-900/50 rounded-full blur-2xl absolute top-4 shadow-[0_0_40px_rgba(16,185,129,0.8)] animate-pulse" />
                            <AndroidHead className="w-24 h-24 drop-shadow-[0_0_25px_rgba(16,185,129,1)]" color="text-emerald-500" mood="idle" facing="front" />
                        </div>

                        {/* Glow Effect behind input — contained inside the box */}
                        <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/10 via-blue-500/10 to-purple-500/10 rounded-xl opacity-30 group-hover:opacity-50 blur-sm transition duration-500 z-0" />

                        <form onSubmit={handleSubmit} className="relative z-10">
                            {/* Animated cycling placeholder */}
                            {!input.trim() && !isFocused && (
                                <div
                                    aria-hidden="true"
                                    className={`absolute inset-y-0 left-6 flex items-center pointer-events-none text-slate-500 text-lg md:text-xl transition-opacity duration-300 ${
                                        placeholderFading ? 'opacity-0' : 'opacity-100'
                                    }`}
                                >
                                    {PLACEHOLDER_TEXTS[placeholderIdx]}
                                </div>
                            )}
                            <input
                                type="text"
                                data-testid="landing-query-input"
                                value={input}
                                onChange={(e) => setInput(e.target.value.slice(0, MAX_INPUT_CHARS))}
                                onFocus={handleFocus}
                                onBlur={() => setIsFocused(false)}
                                placeholder=""
                                maxLength={MAX_INPUT_CHARS}
                                className="w-full bg-black/40 backdrop-blur-xl border border-white/10 rounded-xl py-6 pl-6 pr-16 text-lg md:text-xl text-white placeholder-transparent focus:outline-none focus:ring-1 focus:ring-white/20 transition-all shadow-2xl"
                                aria-describedby="char-count"
                            />
                            {/* (#18) Pulse animation on submit button when input has content */}
                            <button
                                type="submit"
                                data-testid="landing-submit"
                                className={`absolute inset-y-2 right-2 px-4 rounded-lg bg-white/10 hover:bg-white/20 text-white transition-all flex items-center justify-center ${input.trim() ? 'opacity-100 translate-x-0 animate-pulse' : 'opacity-0 translate-x-4 pointer-events-none'}`}
                                style={{ animationDuration: '2s' }}
                            >
                                <ArrowRight className="w-5 h-5" />
                            </button>
                        </form>

                        {/* Quick-Start Prompts */}
                        {!hasText && (
                            <div className="mt-6 hidden md:flex flex-col items-center animate-in fade-in slide-in-from-top-4 duration-700 delay-500">
                                <div className="pointer-events-none flex flex-col items-center">
                                    <div className="h-5 w-px bg-gradient-to-b from-cyan-300/60 via-cyan-400/25 to-transparent" />
                                    <div className="-mt-1 h-6 w-24 rounded-full bg-gradient-to-r from-transparent via-cyan-400/25 to-transparent blur-xl" />
                                </div>
                                <div className="mt-3 flex justify-center gap-3">
                                    <button
                                        type="button"
                                        onClick={() => setInput("Our team is misaligned on core priorities for Q3")}
                                        className="px-3 py-1.5 rounded-full border border-white/10 bg-white/5 text-[11px] text-slate-400 hover:text-white hover:bg-white/10 hover:border-cyan-500/30 transition-all"
                                    >
                                        Team Misalignment
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setInput("Technical debt is slowing down feature delivery")}
                                        className="px-3 py-1.5 rounded-full border border-white/10 bg-white/5 text-[11px] text-slate-400 hover:text-white hover:bg-white/10 hover:border-purple-500/30 transition-all"
                                    >
                                        Technical Debt
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setInput("We lack the data to prove our product-market fit")}
                                        className="px-3 py-1.5 rounded-full border border-white/10 bg-white/5 text-[11px] text-slate-400 hover:text-white hover:bg-white/10 hover:border-emerald-500/30 transition-all"
                                    >
                                        Product-Market Fit
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* (#16) Character count indicator */}
                        {input.length > 0 && (
                            <div id="char-count" className="absolute -bottom-6 right-2 text-[10px] font-mono tabular-nums transition-opacity" aria-live="polite">
                                <span className={input.length > MAX_INPUT_CHARS * 0.9 ? 'text-amber-400' : 'text-slate-500'}>
                                    {input.length}/{MAX_INPUT_CHARS}
                                </span>
                            </div>
                        )}
                    </div>

                    <div id="meet-the-crew" className="mt-24 w-full max-w-6xl">
                        <div className="rounded-[2rem] border border-white/8 bg-[linear-gradient(180deg,rgba(9,13,25,0.92),rgba(5,8,16,0.78))] p-5 md:p-7 shadow-[0_45px_140px_-100px_rgba(34,211,238,0.65)]">
                            <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
                                <div>
                                    <div className="text-[11px] uppercase tracking-[0.32em] text-slate-500">Meet the crew</div>
                                    <h2 className="mt-2 text-2xl md:text-3xl font-bold tracking-tight text-white">
                                        Click a specialist and feel how they think.
                                    </h2>
                                </div>
                                <p className="max-w-2xl text-sm md:text-base leading-relaxed text-slate-300">
                                    This is not one generic chatbot with five costumes. Each operator brings a different lens, a different tension, and a different kind of next move.
                                </p>
                            </div>

                            <div className="mt-6 grid gap-3 md:grid-cols-5">
                                {AGENT_LIST.map((agent) => {
                                    const visual = AGENT_VISUALS[agent.id];
                                    const isActive = agent.id === selectedAgentId;
                                    return (
                                        <button
                                            key={agent.id}
                                            type="button"
                                            onClick={() => {
                                                setSelectedAgentId(agent.id);
                                                setHasInteracted(true);
                                            }}
                                            className={`rounded-[1.5rem] border px-4 py-4 text-left transition ${isActive ? `${visual.shellClass} shadow-[0_28px_90px_-54px_rgba(255,255,255,0.38)]` : 'border-white/8 bg-white/[0.025] hover:border-white/18 hover:bg-white/[0.045]'}`}
                                            style={isActive ? { boxShadow: visual.glow } : undefined}
                                        >
                                            <div className="flex items-start justify-between gap-3">
                                                <div
                                                    className={`h-12 w-12 shrink-0 rounded-[1rem] border p-2.5 ${visual.shellClass}`}
                                                    style={{ boxShadow: visual.glow }}
                                                >
                                                    <AndroidHead
                                                        className="h-full w-full"
                                                        color={agent.colorClass}
                                                        mood={visual.mood}
                                                        facing={visual.facing}
                                                    />
                                                </div>
                                                <span className={`rounded-full border px-2.5 py-1 text-[10px] uppercase tracking-[0.24em] ${visual.chipClass}`}>
                                                    {agent.title.split(' ')[0]}
                                                </span>
                                            </div>
                                            <div className="mt-4">
                                                <div className={`text-lg font-bold ${agent.colorClass}`}>{agent.name}</div>
                                                <div className="mt-1 text-xs uppercase tracking-[0.24em] text-slate-500">{agent.title}</div>
                                                <div className={`mt-2 text-[10px] font-mono tracking-wide ${agent.stratum.colorClass} opacity-70`}>{agent.stratum.label}</div>
                                            </div>
                                            <p className="mt-4 text-sm leading-relaxed text-slate-300">
                                                {visual.why}
                                            </p>
                                        </button>
                                    );
                                })}
                            </div>

                            <div
                                className={`mt-6 rounded-[1.8rem] border px-5 py-5 md:px-6 md:py-6 ${selectedAgentVisual.shellClass}`}
                                style={{ boxShadow: `inset 0 1px 0 rgba(255,255,255,0.04), ${selectedAgentVisual.glow}` }}
                            >
                                <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
                                    <div className="flex items-start gap-4">
                                        <div
                                            className={`h-14 w-14 shrink-0 rounded-[1.1rem] border p-3 ${selectedAgentVisual.shellClass}`}
                                            style={{ boxShadow: selectedAgentVisual.glow }}
                                        >
                                            <AndroidHead
                                                className="h-full w-full"
                                                color={selectedAgent.colorClass}
                                                mood={selectedAgentVisual.mood}
                                                facing={selectedAgentVisual.facing}
                                            />
                                        </div>
                                        <div>
                                            <div className="text-[11px] uppercase tracking-[0.32em] text-slate-500">Current voice</div>
                                            <div className={`mt-2 text-2xl font-bold ${selectedAgent.colorClass}`}>{selectedAgent.name}</div>
                                            <div className="text-sm text-slate-400">{selectedAgent.title}</div>
                                            <div className={`mt-1 text-[10px] font-mono tracking-wide ${selectedAgent.stratum.colorClass}`}>{selectedAgent.stratum.label}</div>
                                        </div>
                                    </div>
                                    <div className={`inline-flex items-center rounded-full border px-3 py-1.5 text-[11px] uppercase tracking-[0.28em] ${selectedAgentVisual.chipClass}`}>
                                        Why they jump in
                                    </div>
                                </div>

                                <p className={`mt-5 text-lg md:text-xl leading-relaxed ${selectedAgent.colorClass}`}>
                                    {selectedAgentVisual.signatureQuestion}
                                </p>
                                <p className="mt-3 max-w-3xl text-sm md:text-base leading-relaxed text-slate-200">
                                    {selectedAgentVisual.why}
                                </p>

                                <div className="mt-5 flex flex-wrap gap-3">
                                    <button
                                        type="button"
                                        onClick={() => handleUseScenarioPrompt(selectedAgentVisual.launchPrompt)}
                                        className="inline-flex items-center gap-2 rounded-full bg-white text-slate-950 px-4 py-2 text-sm font-bold transition hover:opacity-90"
                                    >
                                        Ask {selectedAgent.name} to weigh in
                                        <ArrowRight className="h-4 w-4" />
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => handleScenarioSelect(selectedAgentScenario.id)}
                                        className="inline-flex items-center gap-2 rounded-full border border-white/10 px-4 py-2 text-sm font-semibold text-slate-200 transition hover:border-white/20 hover:text-white"
                                    >
                                        See {selectedAgent.name}'s route
                                        <ChevronRight className="h-4 w-4" />
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div> {/* End Hero Wrapper */}


                <section className="mt-24 mb-40 grid w-full gap-8 xl:grid-cols-[0.92fr,1.08fr]">
                    <div className="space-y-6">
                        <div className="rounded-[2rem] border border-white/8 bg-[linear-gradient(180deg,rgba(8,11,19,0.92),rgba(6,8,15,0.84))] p-6 md:p-8 shadow-[0_45px_140px_-100px_rgba(139,92,246,0.6)]">
                            <div className="flex items-start gap-4">
                                <div
                                    className={`h-16 w-16 shrink-0 rounded-[1.4rem] border p-3 ${selectedAgentVisual.shellClass}`}
                                    style={{ boxShadow: selectedAgentVisual.glow }}
                                >
                                    <AndroidHead
                                        className="h-full w-full"
                                        color={selectedAgent.colorClass}
                                        mood={selectedAgentVisual.mood}
                                        facing={selectedAgentVisual.facing}
                                    />
                                </div>
                                <div>
                                    <div className="text-[11px] uppercase tracking-[0.32em] text-slate-500">Crew focus</div>
                                    <h3 className={`mt-2 text-2xl font-bold ${selectedAgent.colorClass}`}>{selectedAgent.name}</h3>
                                    <p className="text-sm text-slate-400">{selectedAgent.title}</p>
                                </div>
                            </div>

                            <p className="mt-6 text-base leading-relaxed text-slate-300">
                                {selectedAgentVisual.why}
                            </p>

                            <div
                                className={`mt-6 rounded-[1.5rem] border px-4 py-4 ${selectedAgentVisual.shellClass}`}
                                style={{ boxShadow: `inset 0 1px 0 rgba(255,255,255,0.04), ${selectedAgentVisual.glow}` }}
                            >
                                <div className="text-[11px] uppercase tracking-[0.28em] text-slate-500">Signature question</div>
                                <p className={`mt-2 text-base leading-relaxed ${selectedAgent.colorClass}`}>
                                    {selectedAgentVisual.signatureQuestion}
                                </p>
                            </div>

                            <div className="mt-6 flex flex-wrap gap-3">
                                <button
                                    type="button"
                                    onClick={() => handleUseScenarioPrompt(selectedAgentVisual.launchPrompt)}
                                    className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/8 px-4 py-2 text-sm font-semibold text-white transition hover:bg-white/12"
                                >
                                    <PlayCircle className="h-4 w-4" />
                                    Ask {selectedAgent.name} this
                                </button>
                                <button
                                    type="button"
                                    onClick={() => handleScenarioSelect(activeScenario.id)}
                                    className="inline-flex items-center gap-2 rounded-full border border-white/10 px-4 py-2 text-sm font-semibold text-slate-300 transition hover:border-white/20 hover:text-white"
                                >
                                    <ChevronRight className="h-4 w-4" />
                                    Reset this route
                                </button>
                            </div>
                        </div>

                        <div className="grid gap-4 md:grid-cols-3">
                            <InfoCard
                                icon={<BrainCircuit className="w-8 h-8 text-cyan-400" />}
                                title="Atomic Breakdowns"
                                desc="Slice big pressure into the actual moving parts, not the story people keep repeating."
                            />
                            <InfoCard
                                icon={<ShieldCheck className="w-8 h-8 text-violet-400" />}
                                title="First Principles"
                                desc="Pressure-test assumptions until the obvious answer stops looking obvious."
                            />
                            <InfoCard
                                icon={<LayoutDashboard className="w-8 h-8 text-emerald-400" />}
                                title="Issue Deconstruction"
                                desc="Map strategy, people, process, tech, and data into one visible decision surface."
                            />
                        </div>
                    </div>

                    <div className="rounded-[2rem] border border-white/8 bg-[linear-gradient(180deg,rgba(10,14,25,0.94),rgba(7,10,18,0.9))] p-6 md:p-8 shadow-[0_55px_160px_-100px_rgba(6,182,212,0.5)]">
                        <div className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
                            <div>
                                <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-[11px] uppercase tracking-[0.28em] text-slate-300">
                                    <Sparkles className="h-3.5 w-3.5 text-cyan-300" />
                                    Try the crew
                                </div>
                                <h2 className="mt-4 text-3xl md:text-4xl font-bold tracking-tight text-white">
                                    Click through the conversation before you commit.
                                </h2>
                                <p className="mt-3 max-w-2xl text-base leading-relaxed text-slate-300">
                                    Each route shows how the crew handoffs work, what each specialist cares about, and how the product turns vague pressure into a sharper next move.
                                </p>
                            </div>
                            <div className="rounded-full border border-white/10 bg-white/[0.04] px-4 py-2 text-sm text-slate-300">
                                {activeScenario.movement}
                            </div>
                        </div>

                        <div className="mt-6 flex flex-wrap gap-2">
                            {HERO_SCENARIOS.map((scenario) => {
                                const isActive = scenario.id === activeScenario.id;
                                return (
                                    <button
                                        key={scenario.id}
                                        type="button"
                                        onClick={() => handleScenarioSelect(scenario.id)}
                                        className={`rounded-full border px-4 py-2 text-sm font-semibold transition ${isActive ? 'border-cyan-400/35 bg-cyan-500/12 text-white shadow-[0_18px_50px_-30px_rgba(34,211,238,0.9)]' : 'border-white/10 bg-white/[0.03] text-slate-300 hover:border-white/20 hover:text-white'}`}
                                    >
                                        {scenario.label}
                                    </button>
                                );
                            })}
                        </div>

                        <div className="mt-8 grid gap-6 lg:grid-cols-[0.92fr,1.08fr]">
                            <div className="rounded-[1.7rem] border border-white/8 bg-black/25 p-5">
                                <div className="text-[11px] uppercase tracking-[0.3em] text-slate-500">{activeScenario.eyebrow}</div>
                                <p className="mt-4 rounded-[1.4rem] border border-white/10 bg-white/[0.04] px-4 py-4 text-base leading-relaxed text-slate-100 shadow-[inset_0_1px_0_rgba(255,255,255,0.04)]">
                                    {activeScenario.prompt}
                                </p>

                                <div className="mt-5 space-y-3">
                                    {activeScenario.outcomes.map((outcome) => (
                                        <div key={outcome} className="flex items-start gap-3 rounded-2xl border border-white/8 bg-white/[0.03] px-4 py-3 text-sm text-slate-300">
                                            <Target className="mt-0.5 h-4 w-4 shrink-0 text-cyan-300" />
                                            <span>{outcome}</span>
                                        </div>
                                    ))}
                                </div>

                                <div className="mt-5 flex flex-wrap gap-3">
                                    <button
                                        type="button"
                                        onClick={() => handleUseScenarioPrompt(activeScenario.launchPrompt)}
                                        className="inline-flex items-center gap-2 rounded-full bg-white text-slate-950 px-4 py-2 text-sm font-bold transition hover:opacity-90"
                                    >
                                        Use this prompt
                                        <ArrowRight className="h-4 w-4" />
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => navigate('/crucible', { state: { initialQuery: activeScenario.launchPrompt } })}
                                        className="inline-flex items-center gap-2 rounded-full border border-white/10 px-4 py-2 text-sm font-semibold text-slate-200 transition hover:border-white/20 hover:text-white"
                                    >
                                        Open the full crew
                                        <ChevronRight className="h-4 w-4" />
                                    </button>
                                </div>
                            </div>

                            <div className="rounded-[1.7rem] border border-white/8 bg-[linear-gradient(180deg,rgba(255,255,255,0.04),rgba(255,255,255,0.02))] p-5">
                                <div className="text-[11px] uppercase tracking-[0.3em] text-slate-500">Simulated handoff</div>
                                <div className="mt-4 space-y-4">
                                    {visibleResponses.map((response, index) => {
                                        const agent = AGENTS[response.agentId];
                                        const visual = AGENT_VISUALS[response.agentId];
                                        return (
                                            <div key={`${response.agentId}-${index}`} className="flex items-start gap-3">
                                                <button
                                                    type="button"
                                                    onClick={() => setSelectedAgentId(response.agentId)}
                                                    className={`mt-1 h-11 w-11 shrink-0 rounded-2xl border p-2.5 transition ${visual.shellClass}`}
                                                    style={{ boxShadow: visual.glow }}
                                                    aria-label={`Focus ${agent.name}`}
                                                >
                                                    <AndroidHead
                                                        className="h-full w-full"
                                                        color={agent.colorClass}
                                                        mood={visual.mood}
                                                        facing={visual.facing}
                                                    />
                                                </button>
                                                <div className="min-w-0 flex-1">
                                                    <div className="flex items-center gap-2">
                                                        <span className={`text-sm font-bold ${agent.colorClass}`}>{agent.name}</span>
                                                        <span className="text-[10px] uppercase tracking-[0.24em] text-slate-500">{agent.title}</span>
                                                    </div>
                                                    <div
                                                        className={`mt-2 rounded-[1.35rem] border px-4 py-4 text-sm leading-relaxed text-slate-200 ${visual.shellClass}`}
                                                        style={{ boxShadow: `inset 0 1px 0 rgba(255,255,255,0.04), ${visual.glow}` }}
                                                    >
                                                        {renderAgentText(response.text)}
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>

                                <div className="mt-5 flex flex-wrap gap-3">
                                    {nextResponse ? (
                                        <button
                                            type="button"
                                            onClick={handleAdvanceConversation}
                                            className="inline-flex items-center gap-2 rounded-full border border-cyan-400/25 bg-cyan-500/10 px-4 py-2 text-sm font-semibold text-cyan-100 transition hover:bg-cyan-500/15"
                                        >
                                            {nextResponse.cta}
                                            <ChevronRight className="h-4 w-4" />
                                        </button>
                                    ) : (
                                        <button
                                            type="button"
                                            onClick={() => handleScenarioSelect(activeScenario.id)}
                                            className="inline-flex items-center gap-2 rounded-full border border-white/10 px-4 py-2 text-sm font-semibold text-slate-200 transition hover:border-white/20 hover:text-white"
                                        >
                                            Replay this conversation
                                            <PlayCircle className="h-4 w-4" />
                                        </button>
                                    )}
                                    <button
                                        type="button"
                                        onClick={() => handleUseScenarioPrompt(activeScenario.launchPrompt)}
                                        className="inline-flex items-center gap-2 rounded-full border border-white/10 px-4 py-2 text-sm font-semibold text-slate-300 transition hover:border-white/20 hover:text-white"
                                    >
                                        Borrow this route
                                        <ArrowRight className="h-4 w-4" />
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

            </main>



            {/* P1.3 — How We Think About Problems */}
            <HowWeThink />

            {/* Footer attribution */}
            <footer className={`mt-16 text-center text-xs text-slate-500 transition-all duration-700 delay-500 ${hasInteracted ? 'opacity-20' : 'opacity-100'}`}>
                <span>Made by{' '}
                    <a
                        href="https://www.linkedin.com/in/johnathonaaron/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-slate-400 hover:text-blue-400 transition-colors underline underline-offset-2"
                    >
                        Johnathon Aaron
                    </a>
                    {' · '}
                    <a
                        href="https://aOS7.dev"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-slate-400 hover:text-cyan-400 transition-colors"
                    >
                        {'{a}OS Framework ↗'}
                    </a>
                </span>
            </footer>

            <GambitInteraction scrollY={scrollY} />
        </div>
    );
}

function InfoCard({ icon, title, desc }: { icon: React.ReactNode, title: string, desc: string }) {
    return (
        <div className="bg-white/5 backdrop-blur-md border border-white/5 p-6 rounded-2xl hover:bg-white/10 transition-colors group cursor-default">
            <div className="mb-4 bg-white/5 w-12 h-12 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                {icon}
            </div>
            <h3 className="text-lg font-bold text-slate-200 mb-2">{title}</h3>
            <p className="text-slate-400 text-sm leading-relaxed">{desc}</p>
        </div>
    );
}

function renderAgentText(text: string) {
    return text.split(AGENT_MENTION_REGEX).map((part, index) => {
        const agent = AGENT_LIST.find((item) => item.name === part);
        if (!agent) return <React.Fragment key={`${part}-${index}`}>{part}</React.Fragment>;
        return (
            <span key={`${part}-${index}`} className={`font-semibold ${agent.colorClass}`}>
                {part}
            </span>
        );
    });
}

function HowWeThink() {
    const [open, setOpen] = React.useState(false);
    const STRATA = [
        { id: 'L7', label: 'L7 · Experience & Intent', desc: 'The human boundary — where requests enter, approvals happen, and responses render.' },
        { id: 'L6', label: 'L6 · Governance & Trust', desc: 'Identity, access, policy, safety, and compliance. The rules layer.' },
        { id: 'L5', label: 'L5 · Observability & Evaluation', desc: 'Tracing, metrics, evals, drift detection. You cannot improve what you cannot see.' },
        { id: 'L4', label: 'L4 · Orchestration & Coordination', desc: 'Agent routing, task sequencing, handoffs. Where complexity either flows or stalls.' },
        { id: 'L3', label: 'L3 · Execution & Tools', desc: 'The action layer — tools called, code run, APIs hit. Where intent meets reality.' },
        { id: 'L2', label: 'L2 · Memory & State', desc: 'Persistence, context windows, vector stores. What the system remembers between steps.' },
        { id: 'L1', label: 'L1 · Runtime & Infrastructure', desc: 'Compute, latency, containers, cost. The ground your system stands on.' },
    ];
    return (
        <div className="mt-24 w-full max-w-3xl mx-auto">
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                className="w-full flex items-center justify-between gap-4 rounded-2xl border border-white/8 bg-white/[0.025] px-6 py-4 text-left transition hover:bg-white/[0.04]"
            >
                <span className="text-sm font-semibold text-slate-300">How we think about problems</span>
                <span className={`text-slate-500 transition-transform duration-300 ${open ? 'rotate-180' : ''}`}>
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                </span>
            </button>
            {open && (
                <div className="mt-2 rounded-2xl border border-white/8 bg-[#050a10] px-6 py-5 space-y-3 animate-in fade-in slide-in-from-top-2 duration-200">
                    <p className="text-sm text-slate-400 leading-relaxed">
                        Every hard problem lives somewhere in a 7-layer stack. The specialists map your challenge to the layers it actually touches — so the fix lands in the right place.
                    </p>
                    <div className="mt-4 space-y-2">
                        {STRATA.map((s) => (
                            <div key={s.id} className="flex gap-3 text-xs">
                                <span className="shrink-0 w-20 font-mono text-slate-500">{s.label.split(' · ')[0]}</span>
                                <span className="text-slate-300 font-semibold w-36 shrink-0">{s.label.split(' · ')[1]}</span>
                                <span className="text-slate-500">{s.desc}</span>
                            </div>
                        ))}
                    </div>
                    <div className="pt-3 border-t border-white/5">
                        <a
                            href="https://aOS7.dev"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1.5 text-xs text-cyan-400 hover:text-cyan-300 transition-colors"
                        >
                            Explore the {'{a}OS7'} reference model
                            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" /></svg>
                        </a>
                    </div>
                </div>
            )}
        </div>
    );
}

function GambitInteraction({ scrollY }: { scrollY: number }) {
    const [gambitStatus, setGambitStatus] = React.useState<'sleeping' | 'startled' | 'scuttling' | 'sleeping-again'>('sleeping');
    const [gambitMessage, setGambitMessage] = React.useState('');
    const [gambitWaveY, setGambitWaveY] = React.useState(0);
    const [gambitAnchor, setGambitAnchor] = React.useState<'home' | 'follow'>('home');
    const [gambitPos, setGambitPos] = React.useState<{ x: number; y: number }>({ x: 0, y: 0 });
    const [hasMeasuredAnchors, setHasMeasuredAnchors] = React.useState(false);
    const [hasScrollBumped, setHasScrollBumped] = React.useState(false);

    const homeAnchorRef = React.useRef<HTMLDivElement | null>(null);
    const followAnchorRef = React.useRef<HTMLDivElement | null>(null);
    const timeoutsRef = React.useRef<number[]>([]);
    const walkRafRef = React.useRef<number | null>(null);
    const walkStartRef = React.useRef<number>(0);

    const clearScheduled = React.useCallback(() => {
        for (const timeout of timeoutsRef.current) window.clearTimeout(timeout);
        timeoutsRef.current = [];
    }, []);

    const stopWalkingAnimation = React.useCallback(() => {
        if (walkRafRef.current != null) window.cancelAnimationFrame(walkRafRef.current);
        walkRafRef.current = null;
        setGambitWaveY(0);
    }, []);

    const startWalkingAnimation = React.useCallback(() => {
        stopWalkingAnimation();
        walkStartRef.current = window.performance.now();

        const loop = (time: number) => {
            const elapsed = time - walkStartRef.current;
            const ramp = Math.min(1, elapsed / 280);
            const wave = Math.sin((elapsed / 1000) * Math.PI * 2.35) * 3 * ramp;
            setGambitWaveY(wave);
            walkRafRef.current = window.requestAnimationFrame(loop);
        };

        walkRafRef.current = window.requestAnimationFrame(loop);
    }, [stopWalkingAnimation]);

    const measureAnchors = React.useCallback(() => {
        const home = homeAnchorRef.current?.getBoundingClientRect();
        const follow = followAnchorRef.current?.getBoundingClientRect();
        const activeAnchor = gambitAnchor === 'home' ? home : follow;
        if (!activeAnchor) return;

        const next = { x: activeAnchor.left, y: activeAnchor.top };
        setGambitPos((prev) => (prev.x === next.x && prev.y === next.y ? prev : next));
        setHasMeasuredAnchors(true);
    }, [gambitAnchor]);

    const triggerScrollBump = React.useCallback(() => {
        if (hasScrollBumped) return;

        clearScheduled();
        setHasScrollBumped(true);
        setGambitStatus('startled');
        setGambitMessage('HEY!');

        timeoutsRef.current.push(window.setTimeout(() => {
            setGambitStatus('scuttling');
            setGambitMessage("hold up, I'm walkin here");
            setGambitAnchor('follow');
            startWalkingAnimation();
        }, 700));

        timeoutsRef.current.push(window.setTimeout(() => {
            stopWalkingAnimation();
            setGambitStatus('sleeping-again');
        }, 3400));

        timeoutsRef.current.push(window.setTimeout(() => {
            setGambitMessage('');
            setGambitStatus('sleeping');
        }, 5200));
    }, [clearScheduled, hasScrollBumped, startWalkingAnimation, stopWalkingAnimation]);

    React.useEffect(() => {
        return () => {
            clearScheduled();
            stopWalkingAnimation();
        };
    }, [clearScheduled, stopWalkingAnimation]);

    useLayoutEffect(() => {
        measureAnchors();
    }, [measureAnchors]);

    React.useEffect(() => {
        measureAnchors();

        if (hasScrollBumped) return;
        const home = homeAnchorRef.current?.getBoundingClientRect();
        if (!home) return;
        if (home.top <= 72) triggerScrollBump();
    }, [hasScrollBumped, measureAnchors, scrollY, triggerScrollBump]);

    React.useEffect(() => {
        let raf = 0;
        const onResize = () => {
            window.cancelAnimationFrame(raf);
            raf = window.requestAnimationFrame(() => measureAnchors());
        };
        window.addEventListener('resize', onResize);
        return () => {
            window.cancelAnimationFrame(raf);
            window.removeEventListener('resize', onResize);
        };
    }, [measureAnchors]);

    const handleGambitClick = () => {
        if (gambitStatus === 'scuttling') return;

        clearScheduled();
        setGambitStatus('startled');
        setGambitMessage(hasScrollBumped ? 'still here.' : 'huh? who?!');

        timeoutsRef.current.push(window.setTimeout(() => {
            setGambitMessage('');
            setGambitStatus('sleeping-again');
        }, 1600));

        timeoutsRef.current.push(window.setTimeout(() => {
            setGambitStatus('sleeping');
        }, 3200));
    };

    const isGambitAtRest = gambitStatus === 'sleeping' || gambitStatus === 'sleeping-again';
    const isGambitStartled = gambitStatus === 'startled';
    const isGambitScuttling = gambitStatus === 'scuttling';

    return (
        <>
            <div className="absolute left-10 top-[calc(100vh-10rem)] z-0 flex items-end gap-2 pointer-events-none">
                <div className="relative w-10 h-24 mr-2 flex flex-col items-center opacity-80 transition-opacity">
                    <div className="w-8 h-10 bg-cyan-400/30 rounded-t-xl rounded-b-md border border-cyan-300/20 backdrop-blur-sm animate-pulse-slow">
                        <div className="w-full h-2 bg-white/10 mt-2" />
                    </div>
                    <div className="w-8 h-12 bg-slate-800 rounded-b-lg border-x border-b border-white/10 flex flex-col items-center justify-center gap-1">
                        <div className="w-4 h-1 bg-red-500/50 rounded-full" />
                        <div className="w-4 h-1 bg-blue-500/50 rounded-full" />
                    </div>
                </div>

                <div ref={homeAnchorRef} className="h-14 w-14 opacity-0" aria-hidden="true" />
                <div data-testid="landing-bot-mira" className="pointer-events-none absolute h-0 w-0 opacity-0" aria-hidden="true" />
            </div>

            <div ref={followAnchorRef} className="fixed bottom-16 left-[4.75rem] h-14 w-14 pointer-events-none opacity-0" aria-hidden="true" />

            <div
                data-testid="landing-bot-gambit"
                onClick={handleGambitClick}
                className="fixed top-0 left-0 z-40 pointer-events-auto will-change-transform"
                style={{
                    transform: `translate3d(${gambitPos.x}px, ${gambitPos.y}px, 0)`,
                    transitionDuration: hasMeasuredAnchors ? (isGambitScuttling ? '2700ms' : '0ms') : '0ms',
                    transitionTimingFunction: isGambitScuttling ? 'cubic-bezier(0.22, 1, 0.36, 1)' : 'linear',
                }}
            >
                <div className={`${(!isGambitScuttling && !isGambitStartled) ? 'animate-float' : ''}`}>
                    <div
                        className="group cursor-pointer w-14 h-14"
                        style={{
                            transform: `translate3d(0, ${(isGambitStartled ? -24 : 0) + (isGambitScuttling ? gambitWaveY : 0)}px, 0) rotate(${isGambitAtRest ? 10 : 0}deg) scale(${isGambitStartled ? 1.1 : 1})`,
                            transition: 'transform 220ms ease-out',
                        }}
                    >
                        {gambitMessage && (
                            <div
                                data-testid="landing-gambit-message"
                                className="absolute -top-12 right-0 whitespace-nowrap bg-purple-500 text-white text-[10px] font-bold px-3 py-1 rounded-full rounded-br-none shadow-lg z-50"
                            >
                                {gambitMessage}
                            </div>
                        )}

                        {isGambitAtRest && gambitAnchor === 'home' && !gambitMessage && (
                            <div className="absolute -top-6 -right-2 text-purple-300 font-bold text-xs animate-bounce" style={{ animationDuration: '4.5s' }}>
                                Zzz...
                            </div>
                        )}

                        <div className="w-full h-full p-2 bg-black/40 backdrop-blur-md border border-white/10 rounded-full shadow-[0_0_20px_rgba(168,85,247,0.2)] hover:border-purple-400 transition-all flex items-center justify-center">
                            <AndroidHead
                                className="w-full h-full"
                                color="text-purple-400"
                                facing="right"
                                mood={isGambitAtRest ? 'idle' : 'talking'}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}

export default LandingPage;
